import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Filter, 
  Search, 
  Sparkles, 
  Layers, 
  RotateCcw, 
  SlidersHorizontal,
  ChevronRight
} from "lucide-react";
import apiClient from "@/utils/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const categoryParam = searchParams.get("category") || "all";
  const searchParam = searchParams.get("search") || "";
  const sortParam = searchParams.get("sort") || "newest";
  const completeSetOnly = searchParams.get("complete_set") === "true";

  const [searchInput, setSearchInput] = useState(searchParam);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [sortBy, setSortBy] = useState(sortParam);
  const [filterCompleteSet, setFilterCompleteSet] = useState(completeSetOnly);

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      if (searchParam) {
        params.append("search", searchParam);
      }
      if (sortBy) {
        params.append("sort_by", sortBy);
      }

      const [prodRes, catRes] = await Promise.all([
        apiClient.get(`/products?${params.toString()}`),
        apiClient.get("/categories")
      ]);

      let list = prodRes.data || [];
      if (filterCompleteSet) {
        list = list.filter((p) => p.is_complete_set);
      }
      setProducts(list);
      setCategories(catRes.data || []);
    } catch (err) {
      console.error("Shop fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (newCat, newSearch, newSort, newCompleteSet) => {
    const params = new URLSearchParams();
    if (newCat && newCat !== "all") params.set("category", newCat);
    if (newSearch) params.set("search", newSearch);
    if (newSort) params.set("sort", newSort);
    if (newCompleteSet) params.set("complete_set", "true");
    setSearchParams(params);
  };

  const handleCategorySelect = (catName) => {
    setSelectedCategory(catName);
    applyFilters(catName, searchInput, sortBy, filterCompleteSet);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    applyFilters(selectedCategory, searchInput, sortBy, filterCompleteSet);
  };

  const handleSortChange = (e) => {
    const val = e.target.value;
    setSortBy(val);
    applyFilters(selectedCategory, searchInput, val, filterCompleteSet);
  };

  const handleCompleteSetToggle = () => {
    const nextVal = !filterCompleteSet;
    setFilterCompleteSet(nextVal);
    applyFilters(selectedCategory, searchInput, sortBy, nextVal);
  };

  const handleResetFilters = () => {
    setSelectedCategory("all");
    setSearchInput("");
    setSortBy("newest");
    setFilterCompleteSet(false);
    setSearchParams({});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      
      {/* Page Header */}
      <div className="bg-[#FAF8F5] p-6 sm:p-8 rounded-2xl border border-[#E5E0D8]">
        <div className="flex items-center gap-2 text-xs text-stone-500 mb-2">
          <span>Home</span>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#996515] font-semibold">Lace Catalog</span>
          {selectedCategory !== "all" && (
            <>
              <ChevronRight className="w-3 h-3" />
              <span className="text-stone-800 font-bold">{selectedCategory}</span>
            </>
          )}
        </div>
        <h1 
          data-testid="shop-page-title"
          className="font-heading text-3xl sm:text-4xl font-bold text-stone-900"
        >
          {selectedCategory === "all" ? "All Fancy Laces & Complete Sets" : selectedCategory}
        </h1>
        <p className="text-xs sm:text-sm text-stone-600 mt-2 max-w-2xl">
          Browse our original China-imported laces, diamond sitara borders, organza patches, and full dress sets available for retail delivery & wholesale bulk order.
        </p>
      </div>

      {/* Main Filter & Products Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        
        {/* Left Filter Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-5 rounded-xl border border-[#E5E0D8] shadow-sm space-y-5">
            
            <div className="flex items-center justify-between border-b border-[#E5E0D8] pb-3">
              <h3 className="font-heading font-bold text-sm text-stone-900 flex items-center gap-1.5">
                <SlidersHorizontal className="w-4 h-4 text-[#D4AF37]" />
                Filters
              </h3>
              {(selectedCategory !== "all" || searchInput || filterCompleteSet) && (
                <button
                  onClick={handleResetFilters}
                  data-testid="btn-reset-filters"
                  className="text-[11px] text-[#996515] hover:underline flex items-center gap-1 font-medium"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset
                </button>
              )}
            </div>

            {/* Complete Set Only Filter Toggle */}
            <div className="bg-[#FAF8F5] p-3 rounded-lg border border-[#D4AF37]/40">
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-bold text-stone-900">Complete Sets Only</span>
                </div>
                <input
                  type="checkbox"
                  data-testid="filter-complete-set-checkbox"
                  checked={filterCompleteSet}
                  onChange={handleCompleteSetToggle}
                  className="w-4 h-4 text-[#D4AF37] rounded border-stone-300 focus:ring-[#D4AF37]"
                />
              </label>
              <p className="text-[10px] text-stone-500 mt-1">
                Show only Complete Set packs with defined Colors, Taan & Gaz.
              </p>
            </div>

            {/* Category Selector */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-stone-800 mb-3">
                Categories
              </h4>
              <div className="space-y-1">
                <button
                  type="button"
                  data-testid="filter-category-all"
                  onClick={() => handleCategorySelect("all")}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition flex items-center justify-between ${
                    selectedCategory === "all"
                      ? "bg-[#1C1917] text-white font-bold"
                      : "text-stone-700 hover:bg-[#FAF8F5]"
                  }`}
                >
                  <span>All Categories</span>
                  <span className="text-[10px] opacity-70">{products.length}</span>
                </button>

                {categories.map((cat) => (
                  <button
                    key={cat.id || cat.slug}
                    type="button"
                    data-testid={`filter-category-${cat.slug}`}
                    onClick={() => handleCategorySelect(cat.name)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition flex items-center justify-between ${
                      selectedCategory === cat.name
                        ? "bg-[#1C1917] text-white font-bold"
                        : "text-stone-700 hover:bg-[#FAF8F5]"
                    }`}
                  >
                    <span className="truncate pr-2">{cat.name}</span>
                    <span className="text-[10px] opacity-70">
                      {cat.item_count ? `${cat.item_count}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* Right Products Catalog */}
        <div className="lg:col-span-3 space-y-6">
          
          {/* Top Bar: Search & Sort Controls */}
          <div className="bg-white p-4 rounded-xl border border-[#E5E0D8] shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            
            {/* Search within shop */}
            <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-80">
              <input
                type="text"
                data-testid="shop-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search laces, sitara, codes..."
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
              />
              <Search className="w-3.5 h-3.5 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </form>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <span className="text-xs text-stone-500 whitespace-nowrap">Sort by:</span>
              <select
                data-testid="shop-sort-select"
                value={sortBy}
                onChange={handleSortChange}
                className="bg-[#FAF8F5] border border-stone-300 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#D4AF37] font-medium"
              >
                <option value="newest">Newest Arrivals</option>
                <option value="price_asc">Price: Low to High (PKR)</option>
                <option value="price_desc">Price: High to Low (PKR)</option>
                <option value="wholesale_price">Wholesale Price (PKR)</option>
              </select>
            </div>
          </div>

          {/* Active Filter Tags */}
          {(selectedCategory !== "all" || searchParam || filterCompleteSet) && (
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-stone-500">Active filters:</span>
              {selectedCategory !== "all" && (
                <span className="px-2.5 py-1 bg-[#D4AF37]/20 text-[#996515] rounded-full font-medium">
                  {selectedCategory}
                </span>
              )}
              {searchParam && (
                <span className="px-2.5 py-1 bg-stone-200 text-stone-800 rounded-full font-medium">
                  Keyword: "{searchParam}"
                </span>
              )}
              {filterCompleteSet && (
                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                  Complete Sets
                </span>
              )}
            </div>
          )}

          {/* Products Count */}
          <div className="text-xs text-stone-500">
            Showing <span className="font-bold text-stone-800">{products.length}</span> products
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="py-20 text-center text-stone-400 text-sm">
              Loading lace collections...
            </div>
          ) : products.length === 0 ? (
            <div 
              data-testid="no-products-found"
              className="bg-white rounded-2xl border border-[#E5E0D8] p-12 text-center space-y-4"
            >
              <div className="w-16 h-16 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="font-heading font-bold text-lg text-stone-900">No lace products found</h3>
              <p className="text-xs text-stone-500 max-w-sm mx-auto">
                No designs match your active search or filters. Try clearing filters or searching for terms like "sitara", "organza", or "galla".
              </p>
              <button
                onClick={handleResetFilters}
                className="px-5 py-2.5 bg-[#1C1917] text-white rounded-lg text-xs font-semibold hover:bg-[#D4AF37] hover:text-[#1C1917] transition"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div 
              data-testid="shop-products-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
